import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { canManageAccess, type ProblemReport } from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote } from '@/components/ui'
import { formatDateTime } from '@/lib/dates'
import { humanErrorMessage } from '@/lib/errorMessage'
import { clearProblemReports, fetchProblemReports } from '@/lib/problemReports'
import { fetchAllowedUsers } from './accessApi'

/**
 * Failures other people hit, so somebody hears about them.
 *
 * The case for it: a colleague's receipt uploads were refused for a month
 * before anyone knew. The cause took a minute to fix. What cost the month was
 * that the app told him, in words he could not act on, and told nobody else at
 * all — and the people most likely to be stuck are the least likely to say so,
 * because they assume the fault is theirs.
 *
 * Shown to whoever can act: the owner and any administrator. Deliberately dull
 * — a code, a context, who, when. Anything richer would be a debugging console
 * for a screen that should usually be empty.
 */
export function ProblemsCard({ email }: { email: string }) {
  const client = useQueryClient()

  const { data: allowed = [] } = useQuery({
    queryKey: ['allowedUsers'],
    queryFn: fetchAllowedUsers,
  })

  const canSee = canManageAccess(email, allowed)

  const { data: reports = [], error } = useQuery({
    queryKey: ['problemReports'],
    queryFn: () => fetchProblemReports(),
    // Asking for these as somebody who may not read them produces a permission
    // error, which this card would then report — a loop of its own making.
    enabled: canSee,
  })

  const clear = useMutation({
    mutationFn: () => clearProblemReports(reports),
    onSuccess: () => client.invalidateQueries({ queryKey: ['problemReports'] }),
  })

  if (!canSee) return null

  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-medium text-slate-900">Problems people have hit</h2>
          <p className="mt-1 text-sm text-slate-600">
            Failures that don&apos;t fix themselves — a missing permission, an
            expired session, a limit reached. Recorded when they happen, so
            nobody has to think to mention them.
          </p>
        </div>
        {reports.length > 0 && (
          <Button onClick={() => clear.mutate()} disabled={clear.isPending}>
            {clear.isPending ? 'Clearing…' : `Clear ${reports.length}`}
          </Button>
        )}
      </div>

      {error != null && (
        <ErrorNote>{humanErrorMessage(error, "Couldn't load the problem list.")}</ErrorNote>
      )}

      {reports.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Nothing reported. This is the state to expect.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {reports.map((report) => (
            <ProblemRow key={report.id} report={report} />
          ))}
        </ul>
      )}
    </Card>
  )
}

/** What a code actually means for the person who hit it. */
function meaningOf(code: string): string | null {
  switch (code) {
    case 'permission-denied':
    case 'storage/unauthorized':
    case 'functions/permission-denied':
      return 'Refused by the security rules — check their access, and the IAM grants in PROJECT-SETUP.md.'
    case 'unauthenticated':
    case 'storage/unauthenticated':
    case 'functions/unauthenticated':
      return 'Their sign-in had expired. Harmless unless it keeps happening.'
    case 'failed-precondition':
      return 'A query ran without the index it needs — deploy the rules and indexes.'
    case 'resource-exhausted':
    case 'storage/quota-exceeded':
      return 'A quota or spending limit was reached.'
    default:
      return null
  }
}

function ProblemRow({ report }: { report: ProblemReport }) {
  const meaning = meaningOf(report.code)

  return (
    <li className="space-y-1 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="warn">{report.code}</Badge>
        <span className="text-sm text-slate-900">{report.context}</span>
      </div>
      <p className="text-xs text-slate-500">
        {report.email} · {formatDateTime(report.at)}
      </p>
      {meaning !== null && <p className="text-xs text-slate-600">{meaning}</p>}
    </li>
  )
}
